import { pointsBounds, unionRects } from "./geometry.js";
const REMOTE_CURSOR_ACCENT = "#0084ce";
const REMOTE_CURSOR_PILL = "#eff5ff";
const REMOTE_CURSOR_TEXT = "#171719";
const REMOTE_CURSOR_DOT_OUTLINE = "#ffffff";
const REMOTE_CURSOR_BASE_FONT_SIZE = 16;
const REMOTE_CURSOR_BASE_DOT_RADIUS = 7;
const REMOTE_CURSOR_BASE_PILL_HEIGHT = 32;
const REMOTE_CURSOR_BASE_PILL_PADDING = 10;
const REMOTE_CURSOR_MAX_SCALE = 1.5;
const REMOTE_CURSOR_MARGIN = 4;
const REMOTE_CURSOR_SETTLE_MS = 20;
const REMOTE_CURSOR_DECAY = 4.6;
export class WhiteboardCanvasController {
    canvas;
    model;
    #environment;
    #reducedMotion;
    #staticCanvas;
    #staticContext;
    #context;
    #createId;
    #now;
    #onCursor;
    #onPreview;
    #onError;
    #pathCache = new Map();
    #activePage;
    #editable;
    #visible = true;
    #tool = "pen";
    #color = "#17323d";
    #width = 3;
    #logicalWidth = 1;
    #logicalHeight = 1;
    #devicePixelRatio = 1;
    #drawingPoints;
    #gestureWidth;
    #eraseLastPoint;
    #eraseCandidates;
    #activePointerId;
    #remoteStates = [];
    #displayedRemoteCursors = new Map();
    #drawnOrder = [];
    #pageObserver;
    #pagesObserver;
    #frame;
    #previousOverlayBounds;
    #needsRebuild = true;
    #bitmap;
    #bitmapGeneration = 0;
    #destroyed = false;
    #removeInputListeners = [];
    constructor(options) {
        this.canvas = options.canvas;
        this.model = options.model;
        this.#activePage = options.activePage;
        this.#editable = options.editable;
        this.#environment = options.environment;
        this.#reducedMotion = options.reducedMotion === true;
        this.#staticCanvas = this.#environment.createCanvas();
        const context = this.canvas.getContext("2d", { desynchronized: true });
        const staticContext = this.#staticCanvas.getContext("2d");
        if (context === null || staticContext === null)
            throw new Error("whiteboard requires a 2D canvas context");
        this.#context = context;
        this.#staticContext = staticContext;
        this.#createId =
            options.createId ??
                (() => {
                    throw new Error("whiteboard editing requires an injected random ID source");
                });
        this.#now = options.now ?? Date.now;
        this.#onCursor = options.onCursor ?? (() => undefined);
        this.#onPreview = options.onPreview ?? (() => undefined);
        this.#onError = options.onError ?? (() => undefined);
        this.resize();
        this.#bindPage();
        this.#bindInput();
        this.#rebuildStaticLayer();
    }
    resize() {
        const viewport = this.#environment.viewport();
        this.#logicalWidth = Math.max(1, viewport.width);
        this.#logicalHeight = Math.max(1, viewport.height);
        this.#devicePixelRatio = Math.max(1, viewport.devicePixelRatio);
        const width = Math.round(this.#logicalWidth * this.#devicePixelRatio);
        const height = Math.round(this.#logicalHeight * this.#devicePixelRatio);
        this.canvas.width = width;
        this.canvas.height = height;
        this.#staticCanvas.width = width;
        this.#staticCanvas.height = height;
        this.#needsRebuild = true;
        if (this.#visible)
            this.#rebuildStaticLayer();
    }
    setActivePage(pageId) {
        if (this.#activePage === pageId)
            return;
        this.#endEraseGestureIfActive();
        this.#activePointerId = undefined;
        this.#activePage = pageId;
        this.#displayedRemoteCursors.clear();
        this.#drawingPoints = undefined;
        this.#onPreview(undefined);
        this.#bindPage();
        this.#needsRebuild = true;
        if (this.#visible)
            this.#rebuildStaticLayer();
    }
    setEditable(editable) {
        this.#editable = editable;
        if (editable)
            return;
        this.#endEraseGestureIfActive();
        this.#activePointerId = undefined;
        this.#drawingPoints = undefined;
        this.#onPreview(undefined);
        this.#scheduleOverlay();
    }
    setVisible(visible) {
        if (this.#visible === visible)
            return;
        this.#visible = visible;
        if (!visible) {
            if (this.#frame !== undefined)
                this.#environment.cancelAnimationFrame(this.#frame);
            this.#frame = undefined;
            this.#displayedRemoteCursors.clear();
            return;
        }
        if (this.#needsRebuild)
            this.#rebuildStaticLayer();
        else
            this.#scheduleOverlay(true);
    }
    setTool(tool) {
        this.#tool = tool;
    }
    setColor(color) {
        if (!/^#[0-9a-f]{6}$/iu.test(color))
            throw new Error("whiteboard colors must be #rrggbb");
        this.#color = color.toLowerCase();
    }
    setWidth(width) {
        if (!Number.isFinite(width) || width <= 0)
            throw new Error("whiteboard width must be positive");
        this.#width = width;
    }
    setRemoteAwareness(states) {
        this.#displayedRemoteCursors.clear();
        this.#setRemoteAwarenessWithIdentity(states.map((state, index) => [index, state]));
    }
    /** Presentation-only stable identity used for cursor interpolation. */
    setRemoteAwarenessWithIdentity(states) {
        this.#setRemoteAwarenessWithIdentity(states);
    }
    #setRemoteAwarenessWithIdentity(states) {
        this.#remoteStates = states;
        const activeClientIds = new Set(states.map(([clientId]) => clientId));
        for (const clientId of this.#displayedRemoteCursors.keys()) {
            if (!activeClientIds.has(clientId))
                this.#displayedRemoteCursors.delete(clientId);
        }
        for (const [clientId, state] of states) {
            const cursor = state.cursor;
            const displayed = this.#displayedRemoteCursors.get(clientId);
            if (cursor === undefined) {
                this.#displayedRemoteCursors.delete(clientId);
                continue;
            }
            if (displayed === undefined || displayed.page !== cursor.page) {
                this.#displayedRemoteCursors.set(clientId, {
                    page: cursor.page,
                    x: cursor.x,
                    y: cursor.y,
                    targetX: cursor.x,
                    targetY: cursor.y,
                    transitionStartedAt: undefined,
                    lastFrameAt: undefined,
                });
                continue;
            }
            if (displayed.targetX === cursor.x && displayed.targetY === cursor.y)
                continue;
            displayed.targetX = cursor.x;
            displayed.targetY = cursor.y;
            displayed.transitionStartedAt = undefined;
            displayed.lastFrameAt = undefined;
        }
        this.#scheduleOverlay();
    }
    rebuild() {
        this.#needsRebuild = true;
        if (this.#visible)
            this.#rebuildStaticLayer();
    }
    /**
     * Returns an isolated, committed-only bitmap for export. The clone is made
     * synchronously so later Yjs updates and live awareness cannot change it.
     */
    snapshotCommittedCanvas() {
        this.#rebuildStaticLayer(true);
        const snapshot = this.#environment.createCanvas();
        snapshot.width = this.#staticCanvas.width;
        snapshot.height = this.#staticCanvas.height;
        const context = snapshot.getContext("2d");
        if (context === null)
            throw new Error("whiteboard export requires a 2D canvas context");
        context.drawImage(this.#staticCanvas, 0, 0);
        return snapshot;
    }
    destroy() {
        this.#endEraseGestureIfActive();
        this.#destroyed = true;
        this.#pageObserver?.();
        this.#pagesObserver?.();
        this.#pageObserver = undefined;
        this.#pagesObserver = undefined;
        for (const remove of this.#removeInputListeners)
            remove();
        this.#removeInputListeners.length = 0;
        if (this.#frame !== undefined)
            this.#environment.cancelAnimationFrame(this.#frame);
        this.#frame = undefined;
        this.#displayedRemoteCursors.clear();
        this.#bitmap?.close();
        this.#bitmap = undefined;
    }
    #bindPage() {
        this.#pageObserver?.();
        this.#pagesObserver?.();
        this.#pageObserver = this.model.observePage(this.#activePage, (change) => {
            if (!this.#visible) {
                this.#needsRebuild = true;
                return;
            }
            if (change.changes.every(({ action }) => action === "add"))
                this.#applyIncrementalSnapshot();
            else
                this.#rebuildStaticLayer();
        });
        this.#pagesObserver = this.model.observePages(() => {
            if (this.model.pageSnapshot(this.#activePage) === undefined)
                return;
            this.#pageObserver?.();
            this.#pageObserver = this.model.observePage(this.#activePage, (change) => {
                if (!this.#visible)
                    this.#needsRebuild = true;
                else if (change.changes.every(({ action }) => action === "add"))
                    this.#applyIncrementalSnapshot();
                else
                    this.#rebuildStaticLayer();
            });
            if (this.#needsRebuild && this.#visible)
                this.#rebuildStaticLayer();
        });
    }
    #applyIncrementalSnapshot() {
        const snapshot = this.model.pageSnapshot(this.#activePage);
        if (snapshot === undefined) {
            this.#rebuildStaticLayer();
            return;
        }
        const ordered = orderedItems(snapshot);
        const prefixStillValid = this.#drawnOrder.every((key, index) => ordered[index]?.key === key);
        if (!prefixStillValid || ordered.length < this.#drawnOrder.length) {
            this.#rebuildStaticLayer();
            return;
        }
        for (const item of ordered.slice(this.#drawnOrder.length))
            this.#drawItem(this.#staticContext, item);
        this.#drawnOrder = ordered.map(({ key }) => key);
        this.#refreshBitmap();
        this.#scheduleOverlay(true);
    }
    #rebuildStaticLayer(force = false) {
        if ((!this.#visible && !force) || this.#destroyed)
            return;
        this.#needsRebuild = false;
        this.#pathCache.clear();
        resetContext(this.#staticContext);
        this.#staticContext.clearRect(0, 0, this.#staticCanvas.width, this.#staticCanvas.height);
        this.#staticContext.setTransform(this.#devicePixelRatio, 0, 0, this.#devicePixelRatio, 0, 0);
        const snapshot = this.model.pageSnapshot(this.#activePage);
        if (snapshot !== undefined) {
            this.#drawBackground(snapshot);
            const ordered = orderedItems(snapshot);
            for (const item of ordered)
                this.#drawItem(this.#staticContext, item);
            this.#drawnOrder = ordered.map(({ key }) => key);
        }
        else {
            this.#staticContext.fillStyle = "#ffffff";
            this.#staticContext.fillRect(0, 0, this.#logicalWidth, this.#logicalHeight);
            this.#drawnOrder = [];
        }
        this.#refreshBitmap();
        this.#scheduleOverlay(true);
    }
    #drawBackground(snapshot) {
        this.#staticContext.fillStyle = "#ffffff";
        this.#staticContext.fillRect(0, 0, this.#logicalWidth, this.#logicalHeight);
        if (snapshot.meta.background !== "grid")
            return;
        this.#staticContext.strokeStyle = "#dce5e7";
        this.#staticContext.lineWidth = 1;
        this.#staticContext.beginPath();
        for (let x = 0; x <= this.#logicalWidth; x += 32) {
            this.#staticContext.moveTo(x, 0);
            this.#staticContext.lineTo(x, this.#logicalHeight);
        }
        for (let y = 0; y <= this.#logicalHeight; y += 32) {
            this.#staticContext.moveTo(0, y);
            this.#staticContext.lineTo(this.#logicalWidth, y);
        }
        this.#staticContext.stroke();
    }
    #drawItem(context, item) {
        if (item.stroke !== undefined) {
            this.#drawStroke(context, item.id, item.stroke);
            return;
        }
        if (item.object !== undefined)
            this.#drawObject(context, item.object);
    }
    #drawStroke(context, strokeId, stroke) {
        const path = this.#pathCache.get(strokeId) ??
            strokePath(stroke.pts, this.#environment);
        this.#pathCache.set(strokeId, path);
        context.save();
        context.lineCap = "round";
        context.lineJoin = "round";
        context.lineWidth = stroke.width;
        context.strokeStyle =
            stroke.tool === "eraser-stroke" ? "#ffffff" : stroke.color;
        context.globalAlpha = stroke.tool === "highlighter" ? 0.35 : 1;
        context.stroke(path);
        context.restore();
    }
    #drawObject(context, object) {
        context.save();
        context.strokeStyle = object.color;
        context.fillStyle = object.color;
        context.lineWidth = object.width ?? 2;
        const width = object.w ?? 0;
        const height = object.h ?? 0;
        switch (object.kind) {
            case "text":
                context.font = `${Math.max(16, object.width ?? 24)}px sans-serif`;
                context.textBaseline = "top";
                drawMultilineText(context, object.text ?? "", object.x, object.y);
                break;
            case "rect":
                context.strokeRect(object.x, object.y, width, height);
                break;
            case "ellipse":
                context.beginPath();
                context.ellipse(object.x + width / 2, object.y + height / 2, Math.abs(width / 2), Math.abs(height / 2), 0, 0, Math.PI * 2);
                context.stroke();
                break;
            case "line":
            case "arrow":
                drawLineObject(context, object, object.kind === "arrow");
                break;
        }
        context.restore();
    }
    #refreshBitmap() {
        const createBitmap = this.#environment.createImageBitmap;
        if (createBitmap === undefined)
            return;
        this.#bitmap?.close();
        this.#bitmap = undefined;
        const generation = ++this.#bitmapGeneration;
        void createBitmap(this.#staticCanvas)
            .then((bitmap) => {
            if (this.#destroyed || generation !== this.#bitmapGeneration) {
                bitmap.close();
                return;
            }
            this.#bitmap?.close();
            this.#bitmap = bitmap;
            this.#scheduleOverlay(true);
        })
            .catch(() => {
            // The static canvas remains the required baseline.
        });
    }
    #scheduleOverlay(full = false) {
        if (!this.#visible || this.#destroyed)
            return;
        if (full)
            this.#previousOverlayBounds = {
                x: 0,
                y: 0,
                width: this.#logicalWidth,
                height: this.#logicalHeight,
            };
        if (this.#frame !== undefined)
            return;
        this.#frame = this.#environment.requestAnimationFrame((timestamp) => {
            this.#frame = undefined;
            this.#drawOverlay(timestamp);
        });
    }
    #drawOverlay(timestamp) {
        if (!this.#visible || this.#destroyed)
            return;
        const cursorsAnimating = this.#advanceRemoteCursors(timestamp);
        const currentBounds = this.#overlayBounds();
        const dirty = snapRectOutToDevicePixels(unionRects(this.#previousOverlayBounds, currentBounds) ?? {
            x: 0,
            y: 0,
            width: this.#logicalWidth,
            height: this.#logicalHeight,
        }, this.#devicePixelRatio);
        this.#context.save();
        this.#context.setTransform(this.#devicePixelRatio, 0, 0, this.#devicePixelRatio, 0, 0);
        this.#context.beginPath();
        this.#context.rect(dirty.x, dirty.y, dirty.width, dirty.height);
        this.#context.clip();
        this.#context.clearRect(dirty.x, dirty.y, dirty.width, dirty.height);
        resetContext(this.#context);
        this.#context.drawImage(this.#bitmap ?? this.#staticCanvas, 0, 0, this.canvas.width, this.canvas.height);
        this.#context.setTransform(this.#devicePixelRatio, 0, 0, this.#devicePixelRatio, 0, 0);
        this.#drawLiveStroke();
        this.#drawRemoteAwareness();
        this.#context.restore();
        this.#previousOverlayBounds = currentBounds;
        if (cursorsAnimating)
            this.#scheduleOverlay();
    }
    #advanceRemoteCursors(timestamp) {
        let animating = false;
        for (const [clientId, state] of this.#remoteStates) {
            const cursor = state.cursor;
            if (cursor === undefined)
                continue;
            let displayed = this.#displayedRemoteCursors.get(clientId);
            if (displayed === undefined || displayed.page !== cursor.page) {
                displayed = {
                    page: cursor.page,
                    x: cursor.x,
                    y: cursor.y,
                    targetX: cursor.x,
                    targetY: cursor.y,
                    transitionStartedAt: undefined,
                    lastFrameAt: undefined,
                };
                this.#displayedRemoteCursors.set(clientId, displayed);
            }
            if (this.#reducedMotion) {
                displayed.x = displayed.targetX;
                displayed.y = displayed.targetY;
                displayed.transitionStartedAt = undefined;
                displayed.lastFrameAt = undefined;
                continue;
            }
            if (displayed.x === displayed.targetX &&
                displayed.y === displayed.targetY)
                continue;
            if (displayed.transitionStartedAt === undefined) {
                displayed.transitionStartedAt = timestamp;
                displayed.lastFrameAt = timestamp;
                animating = true;
                continue;
            }
            const elapsed = Math.max(0, timestamp - displayed.transitionStartedAt);
            if (elapsed >= REMOTE_CURSOR_SETTLE_MS) {
                displayed.x = displayed.targetX;
                displayed.y = displayed.targetY;
                displayed.transitionStartedAt = undefined;
                displayed.lastFrameAt = undefined;
                continue;
            }
            const delta = Math.max(0, timestamp - (displayed.lastFrameAt ?? timestamp));
            const progress = 1 - Math.exp((-REMOTE_CURSOR_DECAY * delta) / REMOTE_CURSOR_SETTLE_MS);
            displayed.x += (displayed.targetX - displayed.x) * progress;
            displayed.y += (displayed.targetY - displayed.y) * progress;
            displayed.lastFrameAt = timestamp;
            animating = true;
        }
        return animating;
    }
    #drawLiveStroke() {
        const points = this.#drawingPoints;
        if (points === undefined || points.length < 4)
            return;
        this.#context.save();
        this.#context.lineCap = "round";
        this.#context.lineJoin = "round";
        this.#context.lineWidth = this.#gestureWidth ?? this.#width;
        this.#context.strokeStyle =
            this.#tool === "eraser-stroke" ? "#ffffff" : this.#color;
        this.#context.globalAlpha = this.#tool === "highlighter" ? 0.35 : 1;
        this.#context.stroke(strokePath(points, this.#environment));
        this.#context.restore();
    }
    #drawRemoteAwareness() {
        for (const [clientId, state] of this.#remoteStates) {
            if (state.cursor?.page !== this.#activePage)
                continue;
            const preview = state.preview;
            if (preview !== undefined) {
                this.#context.save();
                this.#context.lineCap = "round";
                this.#context.lineJoin = "round";
                this.#context.lineWidth = preview.width;
                this.#context.strokeStyle = preview.color;
                this.#context.globalAlpha =
                    preview.tool === "highlighter" ? 0.35 : 0.72;
                this.#context.stroke(strokePath(preview["pts-tail"], this.#environment));
                this.#context.restore();
            }
            const cursor = this.#displayedRemoteCursors.get(clientId);
            if (cursor === undefined || cursor.page !== this.#activePage)
                continue;
            this.#context.save();
            this.#context.textBaseline = "middle";
            const layout = remoteCursorLayout(this.#context, state.name, cursor.x, cursor.y, this.#logicalWidth, this.#logicalHeight);
            this.#context.fillStyle = REMOTE_CURSOR_PILL;
            this.#context.strokeStyle = REMOTE_CURSOR_ACCENT;
            this.#context.lineWidth = 2;
            traceRoundedRect(this.#context, layout.pill, layout.cornerRadius);
            this.#context.fill();
            this.#context.stroke();
            this.#context.fillStyle = REMOTE_CURSOR_TEXT;
            this.#context.fillText(layout.displayName, layout.pill.x + layout.padding, layout.pill.y + layout.pill.height / 2);
            this.#context.fillStyle = REMOTE_CURSOR_ACCENT;
            this.#context.strokeStyle = REMOTE_CURSOR_DOT_OUTLINE;
            this.#context.lineWidth = 2;
            this.#context.beginPath();
            this.#context.arc(cursor.x, cursor.y, layout.dotRadius, 0, Math.PI * 2);
            this.#context.fill();
            this.#context.stroke();
            this.#context.restore();
        }
    }
    #overlayBounds() {
        let bounds = pointsBounds(this.#drawingPoints ?? [], (this.#gestureWidth ?? this.#width) + 4);
        for (const [clientId, state] of this.#remoteStates) {
            if (state.cursor?.page !== this.#activePage)
                continue;
            if (state.preview !== undefined)
                bounds = unionRects(bounds, pointsBounds(state.preview["pts-tail"], state.preview.width + 4));
            const displayedCursor = this.#displayedRemoteCursors.get(clientId);
            if (displayedCursor?.page === this.#activePage) {
                this.#context.save();
                const cursorBounds = remoteCursorLayout(this.#context, state.name, displayedCursor.x, displayedCursor.y, this.#logicalWidth, this.#logicalHeight).bounds;
                this.#context.restore();
                bounds = unionRects(bounds, cursorBounds);
            }
        }
        return bounds;
    }
    #bindInput() {
        if (this.#environment.supportsPointerEvents?.() === true) {
            this.#listen(this.canvas, "pointerdown", (event) => this.#pointerDown(event));
            this.#listen(this.canvas, "pointermove", (event) => this.#pointerMove(event));
            this.#listen(this.canvas, "pointerup", (event) => this.#pointerUp(event));
            this.#listen(this.canvas, "pointercancel", (event) => this.#pointerCancel(event));
            return;
        }
        this.#listen(this.canvas, "touchstart", (event) => this.#touchStart(event), { passive: false });
        this.#listen(this.canvas, "touchmove", (event) => this.#touchMove(event), { passive: false });
        this.#listen(this.canvas, "touchend", (event) => this.#touchEnd(event));
        this.#listen(this.canvas, "touchcancel", () => this.#cancelGesture());
        this.#listen(this.canvas, "mousedown", (event) => this.#mouseDown(event));
        this.#listen(this.canvas, "mousemove", (event) => this.#mouseMove(event));
        this.#listen(this.canvas, "mouseup", (event) => this.#mouseUp(event));
        this.#listen(this.canvas, "mouseleave", () => this.#cancelGesture());
    }
    #pointerDown(event) {
        if (!this.#editable || event.button !== 0)
            return;
        // One gesture at a time: a palm edge or a second finger landing mid-draw
        // must not restart the gesture or teleport the erase path.
        if (event.isPrimary === false || this.#activePointerId !== undefined)
            return;
        this.#activePointerId = event.pointerId;
        this.canvas.setPointerCapture(event.pointerId);
        this.#startGesture(event.clientX, event.clientY, event.pressure);
    }
    #pointerMove(event) {
        if (!this.#isGesturePointer(event))
            return;
        const samples = event.getCoalescedEvents?.() ?? [event];
        for (const sample of samples)
            this.#moveGesture(sample.clientX, sample.clientY);
    }
    #pointerUp(event) {
        if (!this.#isGesturePointer(event))
            return;
        this.#activePointerId = undefined;
        this.#moveGesture(event.clientX, event.clientY);
        this.#finishGesture();
    }
    #pointerCancel(event) {
        if (this.#activePointerId !== undefined &&
            event.pointerId !== this.#activePointerId)
            return;
        this.#activePointerId = undefined;
        this.#cancelGesture();
    }
    /** The gesture pointer while one is active; the primary pointer otherwise. */
    #isGesturePointer(event) {
        if (this.#activePointerId !== undefined)
            return event.pointerId === this.#activePointerId;
        return event.isPrimary !== false;
    }
    #touchStart(event) {
        if (!this.#editable)
            return;
        if (event.touches.length > 2) {
            this.#cancelGesture();
            return;
        }
        const touch = event.touches[0];
        if (touch === undefined)
            return;
        event.preventDefault();
        this.#startGesture(touch.clientX, touch.clientY, touch.force);
    }
    #touchMove(event) {
        if (event.touches.length > 2) {
            this.#cancelGesture();
            return;
        }
        const touch = event.touches[0];
        if (touch === undefined)
            return;
        event.preventDefault();
        this.#moveGesture(touch.clientX, touch.clientY);
    }
    #touchEnd(event) {
        const touch = event.changedTouches[0];
        if (touch !== undefined)
            this.#moveGesture(touch.clientX, touch.clientY);
        this.#finishGesture();
    }
    #mouseDown(event) {
        if (!this.#editable || event.button !== 0)
            return;
        this.#startGesture(event.clientX, event.clientY);
    }
    #mouseMove(event) {
        this.#moveGesture(event.clientX, event.clientY);
    }
    #mouseUp(event) {
        this.#moveGesture(event.clientX, event.clientY);
        this.#finishGesture();
    }
    #startGesture(clientX, clientY, pressure = 0) {
        const snapshot = this.model.pageSnapshot(this.#activePage);
        if (snapshot === undefined)
            return;
        const point = this.#canvasPoint(clientX, clientY);
        if (this.#tool === "erase-whole") {
            this.#onCursor({ page: this.#activePage, x: point.x, y: point.y });
            this.model.beginEraseGesture();
            this.#eraseLastPoint = point;
            // One snapshot per gesture: hit-testing prunes this list instead of
            // re-sorting the whole page on every pointer sample.
            this.#eraseCandidates = [...snapshot.strokes];
            this.#eraseThrough(point.x, point.y, point.x, point.y);
            this.#scheduleOverlay();
            return;
        }
        this.#gestureWidth =
            pressure > 0 ? this.#width * (0.5 + Math.min(1, pressure)) : this.#width;
        this.#drawingPoints = [point.x, point.y];
        this.#publishTransient(point.x, point.y);
    }
    #moveGesture(clientX, clientY) {
        const point = this.#canvasPoint(clientX, clientY);
        this.#onCursor({ page: this.#activePage, x: point.x, y: point.y });
        const erasePoint = this.#eraseLastPoint;
        if (erasePoint !== undefined) {
            if (this.#tool !== "erase-whole")
                return;
            const eraseDx = point.x - erasePoint.x;
            const eraseDy = point.y - erasePoint.y;
            if (eraseDx * eraseDx + eraseDy * eraseDy < 0.75 * 0.75)
                return;
            this.#eraseLastPoint = point;
            this.#eraseThrough(erasePoint.x, erasePoint.y, point.x, point.y);
            return;
        }
        const points = this.#drawingPoints;
        if (points === undefined)
            return;
        const previousX = points[points.length - 2] ?? point.x;
        const previousY = points[points.length - 1] ?? point.y;
        const dx = point.x - previousX;
        const dy = point.y - previousY;
        if (dx * dx + dy * dy < 0.75 * 0.75)
            return;
        points.push(point.x, point.y);
        this.#publishTransient(point.x, point.y);
    }
    #publishTransient(x, y) {
        this.#onCursor({ page: this.#activePage, x, y });
        if (this.#tool === "erase-whole")
            return;
        const points = this.#drawingPoints ?? [];
        this.#onPreview({
            tool: this.#tool,
            color: this.#color,
            width: this.#gestureWidth ?? this.#width,
            "pts-tail": points.slice(-64),
        });
        this.#scheduleOverlay();
    }
    #finishGesture() {
        if (this.#eraseLastPoint !== undefined) {
            this.#endEraseGestureIfActive();
            this.#scheduleOverlay();
            return;
        }
        const points = this.#drawingPoints;
        this.#drawingPoints = undefined;
        const gestureWidth = this.#gestureWidth ?? this.#width;
        this.#gestureWidth = undefined;
        this.#onPreview(undefined);
        if (this.#tool === "erase-whole" ||
            points === undefined ||
            points.length < 4) {
            this.#scheduleOverlay();
            return;
        }
        try {
            this.model.commitStroke({
                strokeId: this.#createId(),
                pageId: this.#activePage,
                tool: this.#tool,
                color: this.#color,
                width: gestureWidth,
                pts: points,
                createdAt: this.#now(),
            });
        }
        catch (error) {
            this.#onError(error);
        }
        this.#scheduleOverlay();
    }
    #cancelGesture() {
        this.#endEraseGestureIfActive();
        this.#drawingPoints = undefined;
        this.#gestureWidth = undefined;
        this.#onPreview(undefined);
        this.#scheduleOverlay();
    }
    #eraseThrough(x1, y1, x2, y2) {
        const candidates = this.#eraseCandidates;
        if (candidates === undefined || candidates.length === 0)
            return;
        const hits = strokesHitBySegment(candidates, x1, y1, x2, y2);
        if (hits.length === 0)
            return;
        const hitIds = new Set(hits);
        this.#eraseCandidates = candidates.filter(([strokeId]) => !hitIds.has(strokeId));
        this.model.eraseStrokes(this.#activePage, hits);
    }
    #endEraseGestureIfActive() {
        if (this.#eraseLastPoint === undefined)
            return;
        this.#eraseLastPoint = undefined;
        this.#eraseCandidates = undefined;
        this.model.endEraseGesture();
    }
    #canvasPoint(clientX, clientY) {
        const rect = this.canvas.getBoundingClientRect();
        return {
            x: ((clientX - rect.left) * this.#logicalWidth) / Math.max(1, rect.width),
            y: ((clientY - rect.top) * this.#logicalHeight) / Math.max(1, rect.height),
        };
    }
    #listen(target, type, listener, options) {
        target.addEventListener(type, listener, options);
        this.#removeInputListeners.push(() => target.removeEventListener(type, listener, options));
    }
}
function orderedItems(snapshot) {
    return [
        ...snapshot.strokes.map(([id, stroke]) => ({
            key: `stroke:${id}`,
            z: stroke.z,
            id,
            stroke,
        })),
        ...snapshot.objects.map(([id, object]) => ({
            key: `object:${id}`,
            z: object.z,
            id,
            object,
        })),
    ].sort((left, right) => left.z.localeCompare(right.z) || left.id.localeCompare(right.id));
}
function strokePath(points, environment) {
    const path = environment.createPath();
    const firstX = points[0];
    const firstY = points[1];
    if (firstX === undefined || firstY === undefined)
        return path;
    path.moveTo(firstX, firstY);
    for (let index = 2; index + 1 < points.length; index += 2) {
        path.lineTo(points[index] ?? firstX, points[index + 1] ?? firstY);
    }
    return path;
}
/**
 * Every stroke whose painted band touches the erase-path segment. A segment
 * test (not a point test) keeps fast drags from skipping over strokes
 * between sparse pointer samples.
 */
function strokesHitBySegment(strokes, x1, y1, x2, y2, hitSlop = 6) {
    const hits = [];
    for (const [strokeId, stroke] of strokes) {
        const radius = stroke.width / 2 + hitSlop;
        for (let point = 0; point + 3 < stroke.pts.length; point += 2) {
            const startX = stroke.pts[point];
            const startY = stroke.pts[point + 1];
            const endX = stroke.pts[point + 2];
            const endY = stroke.pts[point + 3];
            if (startX !== undefined &&
                startY !== undefined &&
                endX !== undefined &&
                endY !== undefined &&
                segmentToSegmentDistanceSquared(x1, y1, x2, y2, startX, startY, endX, endY) <=
                    radius * radius) {
                hits.push(strokeId);
                break;
            }
        }
    }
    return hits;
}
function segmentToSegmentDistanceSquared(ax, ay, bx, by, cx, cy, dx, dy) {
    if (segmentsProperlyIntersect(ax, ay, bx, by, cx, cy, dx, dy))
        return 0;
    return Math.min(pointToSegmentDistanceSquared(ax, ay, cx, cy, dx, dy), pointToSegmentDistanceSquared(bx, by, cx, cy, dx, dy), pointToSegmentDistanceSquared(cx, cy, ax, ay, bx, by), pointToSegmentDistanceSquared(dx, dy, ax, ay, bx, by));
}
/** Collinear touches are covered by the endpoint-distance fallback above. */
function segmentsProperlyIntersect(ax, ay, bx, by, cx, cy, dx, dy) {
    const d1 = crossOrientation(cx, cy, dx, dy, ax, ay);
    const d2 = crossOrientation(cx, cy, dx, dy, bx, by);
    const d3 = crossOrientation(ax, ay, bx, by, cx, cy);
    const d4 = crossOrientation(ax, ay, bx, by, dx, dy);
    return (((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) &&
        ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0)));
}
function crossOrientation(originX, originY, ax, ay, bx, by) {
    return (ax - originX) * (by - originY) - (ay - originY) * (bx - originX);
}
function pointToSegmentDistanceSquared(x, y, x1, y1, x2, y2) {
    const dx = x2 - x1;
    const dy = y2 - y1;
    const lengthSquared = dx * dx + dy * dy;
    if (lengthSquared === 0)
        return (x - x1) ** 2 + (y - y1) ** 2;
    const position = Math.max(0, Math.min(1, ((x - x1) * dx + (y - y1) * dy) / lengthSquared));
    const nearestX = x1 + position * dx;
    const nearestY = y1 + position * dy;
    return (x - nearestX) ** 2 + (y - nearestY) ** 2;
}
function drawMultilineText(context, text, x, y) {
    const lineHeight = Math.max(18, Number.parseFloat(context.font) * 1.25 || 24);
    for (const [index, line] of text.split(/\r?\n/u).entries())
        context.fillText(line, x, y + index * lineHeight);
}
function drawLineObject(context, object, arrow) {
    const endX = object.x + (object.w ?? 0);
    const endY = object.y + (object.h ?? 0);
    context.beginPath();
    context.moveTo(object.x, object.y);
    context.lineTo(endX, endY);
    if (arrow) {
        const angle = Math.atan2(endY - object.y, endX - object.x);
        const size = Math.max(8, (object.width ?? 2) * 4);
        context.moveTo(endX, endY);
        context.lineTo(endX - Math.cos(angle - Math.PI / 6) * size, endY - Math.sin(angle - Math.PI / 6) * size);
        context.moveTo(endX, endY);
        context.lineTo(endX - Math.cos(angle + Math.PI / 6) * size, endY - Math.sin(angle + Math.PI / 6) * size);
    }
    context.stroke();
}
function remoteCursorLayout(context, name, cursorX, cursorY, canvasWidth, canvasHeight) {
    const scale = clamp(Math.min(canvasWidth / 1_280, canvasHeight / 720), 1, REMOTE_CURSOR_MAX_SCALE);
    const fontSize = Math.round(REMOTE_CURSOR_BASE_FONT_SIZE * scale);
    const font = `600 ${fontSize}px "Pretendard", sans-serif`;
    const dotRadius = REMOTE_CURSOR_BASE_DOT_RADIUS * scale;
    const pillHeightTarget = REMOTE_CURSOR_BASE_PILL_HEIGHT * scale;
    const padding = REMOTE_CURSOR_BASE_PILL_PADDING * scale;
    context.font = font;
    const horizontalMargin = Math.min(REMOTE_CURSOR_MARGIN, canvasWidth / 2);
    const verticalMargin = Math.min(REMOTE_CURSOR_MARGIN, canvasHeight / 2);
    const availableWidth = Math.max(1, canvasWidth - horizontalMargin * 2);
    const normalizedName = name.normalize("NFC");
    const textWidth = measuredTextWidth(context, normalizedName, fontSize);
    const pillWidth = Math.min(availableWidth, Math.max(48 * scale, textWidth + padding * 2));
    const pillHeight = Math.min(pillHeightTarget, Math.max(1, canvasHeight - verticalMargin * 2));
    const pillX = clamp(cursorX + dotRadius + 5 * scale, horizontalMargin, Math.max(horizontalMargin, canvasWidth - horizontalMargin - pillWidth));
    const preferredAbove = cursorY - pillHeight - 10;
    const preferredY = preferredAbove >= verticalMargin
        ? preferredAbove
        : cursorY + dotRadius + 5 * scale;
    const pillY = clamp(preferredY, verticalMargin, Math.max(verticalMargin, canvasHeight - verticalMargin - pillHeight));
    const pill = { x: pillX, y: pillY, width: pillWidth, height: pillHeight };
    const displayName = fitRemoteCursorName(context, normalizedName, Math.max(1, pillWidth - padding * 2), fontSize);
    const dotBounds = {
        x: cursorX - dotRadius - 2,
        y: cursorY - dotRadius - 2,
        width: dotRadius * 2 + 4,
        height: dotRadius * 2 + 4,
    };
    const rawBounds = unionRects(pill, dotBounds) ?? pill;
    const left = clamp(rawBounds.x - 2, 0, canvasWidth);
    const top = clamp(rawBounds.y - 2, 0, canvasHeight);
    const right = clamp(rawBounds.x + rawBounds.width + 2, left, canvasWidth);
    const bottom = clamp(rawBounds.y + rawBounds.height + 2, top, canvasHeight);
    return {
        pill,
        displayName,
        dotRadius,
        padding,
        cornerRadius: 9 * scale,
        bounds: { x: left, y: top, width: right - left, height: bottom - top },
    };
}
function fitRemoteCursorName(context, name, maxWidth, fontSize) {
    if (measuredTextWidth(context, name, fontSize) <= maxWidth)
        return name;
    const codePoints = [...name];
    const ellipsis = "…";
    let minimum = 0;
    let maximum = codePoints.length;
    while (minimum < maximum) {
        const length = Math.ceil((minimum + maximum) / 2);
        const candidate = `${codePoints.slice(0, length).join("")}${ellipsis}`;
        if (measuredTextWidth(context, candidate, fontSize) <= maxWidth)
            minimum = length;
        else
            maximum = length - 1;
    }
    return `${codePoints.slice(0, minimum).join("")}${ellipsis}`;
}
function measuredTextWidth(context, text, fontSize) {
    const measured = context.measureText(text)?.width;
    return typeof measured === "number" && Number.isFinite(measured)
        ? measured
        : Math.max(1, [...text].length) * fontSize;
}
function traceRoundedRect(context, rect, requestedRadius) {
    const radius = Math.min(requestedRadius, Math.max(0, rect.width / 2), Math.max(0, rect.height / 2));
    const right = rect.x + rect.width;
    const bottom = rect.y + rect.height;
    context.beginPath();
    context.moveTo(rect.x + radius, rect.y);
    context.lineTo(right - radius, rect.y);
    context.quadraticCurveTo(right, rect.y, right, rect.y + radius);
    context.lineTo(right, bottom - radius);
    context.quadraticCurveTo(right, bottom, right - radius, bottom);
    context.lineTo(rect.x + radius, bottom);
    context.quadraticCurveTo(rect.x, bottom, rect.x, bottom - radius);
    context.lineTo(rect.x, rect.y + radius);
    context.quadraticCurveTo(rect.x, rect.y, rect.x + radius, rect.y);
    context.closePath();
}
function clamp(value, minimum, maximum) {
    return Math.min(maximum, Math.max(minimum, value));
}
/**
 * Expands a rect outward to whole device pixels. Fractional clip/clearRect
 * edges antialias, leaving semi-transparent seam pixels where the page behind
 * the canvas bleeds through as a gray box tracking the live stroke.
 */
function snapRectOutToDevicePixels(rect, devicePixelRatio) {
    const left = Math.floor(rect.x * devicePixelRatio) / devicePixelRatio;
    const top = Math.floor(rect.y * devicePixelRatio) / devicePixelRatio;
    const right = Math.ceil((rect.x + rect.width) * devicePixelRatio) / devicePixelRatio;
    const bottom = Math.ceil((rect.y + rect.height) * devicePixelRatio) / devicePixelRatio;
    return { x: left, y: top, width: right - left, height: bottom - top };
}
function resetContext(context) {
    context.setTransform(1, 0, 0, 1, 0, 0);
}
//# sourceMappingURL=canvas.js.map